import { useEffect, useMemo, useRef } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

import type { ViewCommand, ViewCommandApi } from '../types/command';

import {
  CommandPaletteViewContext,
  type CommandPaletteViewRuntime,
  CommandPaletteViewRuntimeContext,
} from './command-palette-view-context';

import styles from './command-palette-view-shell.module.css';

export interface CommandPaletteViewShellProps {
  // ViewCommand<unknown> rather than ViewCommand<TPrefill>: the shell
  // is mode-agnostic and renders any view. The per-command useView()
  // factory re-casts the prefill to its declared type at the consumer
  // boundary; the shell carries the runtime value through opaquely.
  command: ViewCommand<unknown>;
  /**
   * Structured prefill the view was opened with. Validated against
   * `command.prefillSchema` before reaching this point (in
   * `actions.openView`), or `undefined` when no schema was declared
   * / no prefill was supplied.
   */
  prefill: unknown;
  /**
   * Monotonic id of the current view entry. Used as a remount key for
   * the body so re-opening the same view with fresh prefill re-seeds
   * mount-time `defaultValues` instead of leaving stale state behind.
   */
  entryId?: number;
  onBack: () => void;
  onClose: () => void;
}

/**
 * Renders the shared header around a view command and provides the
 * public view API plus internal chrome runtime. The command component
 * declaratively composes its body and footer with
 * `<CommandPaletteView>`.
 *
 * The body mounts the command's `Component` inside the view context.
 * The Component identity is read from the command at render time;
 * authors are expected to define it at module scope so React preserves
 * view-internal state across palette re-renders.
 */
export function CommandPaletteViewShell({ command, prefill, entryId, onBack, onClose }: CommandPaletteViewShellProps) {
  // Stable API object per (entry, command). Recomputing it on every
  // render would force every consumer (via the command's useView) to
  // re-render even when nothing it cares about changed. The deps
  // cover view identity and the prefill snapshot: a fresh entry
  // (even of the same command, with different prefill) deserves a
  // fresh api object.
  const api = useMemo<ViewCommandApi<unknown>>(
    () => ({
      close: onClose,
      back: onBack,
      prefill,
    }),
    [onClose, onBack, prefill],
  );
  const primaryActionRef = useRef<HTMLButtonElement>(null);
  const runtime = useMemo<CommandPaletteViewRuntime>(
    () => ({
      command,
      onBack,
      primaryActionRef,
    }),
    [command, onBack],
  );

  // Keyboard handling for view mode. Three shortcuts fire while this
  // shell is mounted:
  //
  //   - mod+Enter   : invoke the currently-installed primary action
  //   - Escape      : back to main mode (mirrors scope/slot exit)
  //   - Shift+Escape: close the palette from any depth
  //
  // Bound on `document` rather than the shell wrapper because focus
  // inside a view isn't guaranteed to be on a focusable descendant:
  // the user might click between inputs (focus -> body) or on the
  // view's background. A shell-bound listener would miss those
  // keydowns since they fire on `body` and never bubble through the
  // shell div. The document-level listener catches them all.
  //
  // We still respect `defaultPrevented` so descendants that handle
  // Escape internally (e.g. a Select popover closing its dropdown)
  // get first crack -- React Aria components mark their own Escape
  // handling via preventDefault, so our handler stays silent on those.
  // The shell only mounts when the palette is in view mode, so the
  // listener's lifecycle naturally scopes to "view is showing."
  //
  useEffect(() => {
    const onKeyDownCapture = (e: KeyboardEvent) => {
      // Nested React Aria overlays consume Escape before it reaches a
      // bubble listener. Shift+Escape is the palette-wide hard close,
      // so intercept it during capture regardless of focused depth.
      if (e.key === 'Escape' && e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (e.key === 'Enter' && isMod && !e.defaultPrevented) {
        const primaryAction = primaryActionRef.current;
        if (
          primaryAction !== null &&
          !primaryAction.disabled &&
          primaryAction.getAttribute('aria-disabled') !== 'true'
        ) {
          e.preventDefault();
          e.stopPropagation();
          primaryAction.click();
        }
        return;
      }

      if (e.key === 'Escape' && !e.defaultPrevented) {
        e.preventDefault();
        e.stopPropagation();
        onBack();
        return;
      }
    };
    document.addEventListener('keydown', onKeyDownCapture, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDownCapture, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onBack, onClose]);

  const Body = command.Component;

  return (
    <CommandPaletteViewContext.Provider value={api}>
      <CommandPaletteViewRuntimeContext.Provider value={runtime}>
        <div>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <TooltipTrigger>
                <IconButton
                  icon="arrow-left-thin"
                  aria-label={msg('commandPalette.view.backLabel')}
                  variant="minimal"
                  size="small"
                  onPress={onBack}
                />
                <Tooltip>
                  <Message id="commandPalette.view.escToGoBack" values={{ kbd: (chunks) => <kbd>{chunks}</kbd> }} />
                </Tooltip>
              </TooltipTrigger>
            </div>
            <div className={styles.headerRight}>
              {command.learnMoreUrl !== undefined && (
                <a className={styles.learnMore} href={command.learnMoreUrl} target="_blank" rel="noreferrer noopener">
                  <Message id="commandPalette.view.learnMore" />
                </a>
              )}
            </div>
          </div>
          {/* Keyed on the entry id so a re-open of the same view with
              fresh prefill remounts the body and re-applies mount-time
              defaults instead of keeping stale field state. */}
          <Body key={entryId} />
        </div>
      </CommandPaletteViewRuntimeContext.Provider>
    </CommandPaletteViewContext.Provider>
  );
}
